import React, { useState, useCallback } from "react";
import styles from "./ImageToPNG.module.css";

const ImageToPNG = () => {
  const [image, setImage] = useState(null);
  const [convertedImage, setConvertedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length) {
      processImage(files[0]);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImage(file);
    }
  };

  const processImage = (file) => {
    setError(null);

    if (!file.type.match("image.*")) {
      setError("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setImage({
          file,
          url: event.target.result,
          name: file.name,
          type: file.type,
          size: file.size,
          dimensions: { width: img.width, height: img.height },
        });
        setConvertedImage(null);
      };
      img.onerror = () => {
        setError("Failed to load image");
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      setError("Failed to read file");
    };
    reader.readAsDataURL(file);
  };

  const convertToPNG = () => {
    if (!image) {
      setError("No image to convert");
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Create white background for images that might have transparency
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        const pngUrl = canvas.toDataURL("image/png");

        // Calculate file size
        const base64String = pngUrl.split(",")[1];
        const fileSize = base64String
          ? Math.round((base64String.length * 3) / 4)
          : 0;

        setConvertedImage({
          url: pngUrl,
          name: `${image.name.split(".")[0]}.png`,
          size: fileSize,
          dimensions: { width: img.width, height: img.height },
        });
      };
      img.onerror = () => {
        setError("Failed to process image");
      };
      img.src = image.url;
    } catch (err) {
      setError("Conversion failed: " + err.message);
    }
  };

  const downloadImage = () => {
    if (!convertedImage) {
      setError("No converted image to download");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = convertedImage.url;
      link.download = convertedImage.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError("Download failed: " + err.message);
    }
  };

  const resetAll = () => {
    setImage(null);
    setConvertedImage(null);
    setError(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 bytes";
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Image to PNG Converter</h1>

      {/* Step Guide */}
      {!image && !convertedImage && (
        <div className={styles.stepsGuide}>
          <h2>How to Convert Images to PNG</h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h4>Upload Image</h4>
                <p>Drag & drop or click to select your image file</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h4>Convert to PNG</h4>
                <p>Click the convert button to transform your image</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h4>Download Result</h4>
                <p>Save your perfectly converted PNG image</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      {!image && !convertedImage && (
        <div className={styles.uploadSection}>
          <div
            className={`${styles.dropArea} ${
              isDragging ? styles.dragOver : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
          >
            <div className={styles.dropContent}>
              <div className={styles.uploadIcon}>🖼️</div>
              <h3 className={styles.dropTitle}>Drop your image here</h3>
              <p className={styles.dropText}>or</p>
              <input
                type="file"
                id="fileInput"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
              <button className={styles.browseButton}>Browse Files</button>
              <p className={styles.supportedFormats}>
                Supports: JPG, WEBP, GIF, BMP, and more
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Content Section */}
      {!image && !convertedImage && (
        <div className={styles.contentSection}>
          <div className={styles.contentGrid}>
            {/* Why PNG Section */}
            <div className={styles.contentBlock}>
              <h2>📘 Why Choose PNG?</h2>
              <p>
                Since its release in 1996, PNG (Portable Network Graphics) has
                proven to be the lossless web graphics solution of choice. PNG
                was created as a patent-free replacement to GIF with impeccable
                quality being preserved and many other enhanced features still
                important in today's digital workflow.
              </p>

              <div className={styles.benefitsGrid}>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🎯</div>
                  <h3>Lossless Quality</h3>
                  <p>
                    Pixels will appear precisely as you intended. Graphics,
                    text, and images you would like to have preserved-sharp
                    quality are still best rendered as PNG files.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🌈</div>
                  <h3>True Transparency</h3>
                  <p>
                    Smooth edges and advanced alpha transparency with 256 levels
                    of opaque color enable many complex transparent effects.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🌐</div>
                  <h3>Universal Support</h3>
                  <p>Browser and image editor support, since the late 90's.</p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>📊</div>
                  <h3>Gamma Correction</h3>
                  <p>
                    A consistent experience of color varied across operating
                    systems and devices.
                  </p>
                </div>
              </div>
            </div>

            {/* Technical Deep Dive */}
            <div className={styles.contentBlock}>
              <h2>⚙️ PNG Technical Advantages</h2>

              <h3>Advanced Compression Technology</h3>
              <p>
                PNG shares a two-stage compression process for maximum reduction
                in file size without loss of quality:
              </p>
              <ul>
                <li>
                  Filters each scanline with one of five filtering algorithms
                </li>
                <li>
                  Uses DEFLATE (similar to ZIP) compression to minimize
                  redundancy
                </li>
              </ul>

              <h3>Color Depth Options</h3>
              <ul>
                <li>
                  <strong>Grayscale:</strong> 1–16 bits per pixel
                </li>
                <li>
                  <strong>Truecolor:</strong> 8–16 bits per color = 24/48 bit
                  RGB
                </li>
                <li>
                  <strong>Indexed Color:</strong> 1–8 bits per pixel
                </li>
                <li>
                  <strong>Alpha Channel:</strong> 8–16 bits of transparency
                </li>
              </ul>
            </div>

            {/* Use Cases */}
            <div className={styles.contentBlock}>
              <h2>🧩 When PNGs Are a Good Option</h2>
              <div className={styles.useCases}>
                <div className={styles.useCaseColumn}>
                  <h3>✅ Good Uses For PNGs</h3>
                  <ul>
                    <li>Company Branding & Logos</li>
                    <li>Elements of User Interfaces</li>
                    <li>Screenshots & Documentation</li>
                    <li>Digital Illustrations</li>
                    <li>Medical & Scientific Images</li>
                    <li>Architectural Plans</li>
                    <li>Legal Evidence (exactly the same copy)</li>
                  </ul>
                </div>
                <div className={styles.useCaseColumn}>
                  <h3>⚠️ You May Want to Try Alternatives for</h3>
                  <ul>
                    <li>Photos - JPG</li>
                    <li>Web Performance - WebP</li>
                    <li>Simple Graphics - SVG</li>
                    <li>Professional Printing - TIFF</li>
                    <li>Animation - GIF or WebP</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* PNG Optimization Strategies */}
            <div className={styles.contentBlock}>
              <h2>🔧 Advanced PNG Optimization Techniques</h2>
              <div className={styles.optimizationGuide}>
                <div className={styles.optimizationTip}>
                  <h4>🎨 Color Reduction</h4>
                  <p>
                    Converting 24-bit PNG into an 8-bit PNG (indexed) will
                    create a smaller file size.
                  </p>
                </div>
                <div className={styles.optimizationTip}>
                  <h4>🔧 Compression Filters</h4>
                  <p>
                    Automatic selection of the best of the five filter types in
                    PNG.
                  </p>
                </div>
                <div className={styles.optimizationTip}>
                  <h4>🗑️ Metadata Stripping</h4>
                  <p>
                    Removing unnecessary EXIF or text chunk metadata to reduce
                    file size.
                  </p>
                </div>
                <div className={styles.optimizationTip}>
                  <h4>📐 Dimension Optimization</h4>
                  <p>
                    Making sure the image to the actual size it will be
                    displayed; avoid unintended bloat.
                  </p>
                </div>
              </div>
            </div>

            {/* PNG-8 vs PNG-24 Guide */}
            <div className={styles.contentBlock}>
              <h2>🧮 PNG-8 vs PNG-24: Choosing the Right File Type</h2>
              <div className={styles.pngComparison}>
                <div className={styles.pngType}>
                  <h3>PNG-8 (Indexed Color)</h3>
                  <ul>
                    <li>
                      <strong>Colors:</strong> 256 maximum
                    </li>
                    <li>
                      <strong>Transparency:</strong> 1 bit
                    </li>
                    <li>
                      <strong>File Size:</strong> Very small
                    </li>
                    <li>
                      <strong>Use For:</strong> Logos, icons, simple graphics
                    </li>
                    <li>
                      <strong>Compression:</strong> Excellent for flat color
                    </li>
                  </ul>
                </div>
                <div className={styles.pngType}>
                  <h3>PNG-24 (True Color)</h3>
                  <ul>
                    <li>
                      <strong>Colors:</strong> 16.7 million
                    </li>
                    <li>
                      <strong>Transparency:</strong> 256 level alpha
                    </li>
                    <li>
                      <strong>File Size:</strong> Large
                    </li>
                    <li>
                      <strong>Use For:</strong> Photographs, gradients, images
                      with more detail
                    </li>
                    <li>
                      <strong>Compression:</strong> Good for detailed images
                    </li>
                  </ul>
                </div>
              </div>
              <p className={styles.note}>
                Note: The converter will select the best format of PNG-8 or
                PNG-24 automatically based on color complexity and whether there
                is transparency.
              </p>
            </div>

            {/* Industry Applications */}
            <div className={styles.contentBlock}>
              <h2>🏢 Uses of PNG in Professional Fields</h2>
              <div className={styles.industryGrid}>
                <div className={styles.industryItem}>
                  <h4>🎮 Game Development</h4>
                  <p>Graphics - sprites and textures</p>
                </div>
                <div className={styles.industryItem}>
                  <h4>📱 App Development</h4>
                  <p>Interface icons and graphics</p>
                </div>
                <div className={styles.industryItem}>
                  <h4>🌐 Web Design</h4>
                  <p>Logos and banners, responsive graphics</p>
                </div>
                <div className={styles.industryItem}>
                  <h4>📊 Business & Marketing</h4>
                  <p>Infographics, presentations, or digital ads</p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className={styles.contentBlock}>
              <h2>❓ Frequently Asked Questions</h2>
              <div className={styles.faqGrid}>
                <div className={styles.faqItem}>
                  <h4>If PNG is bigger, why take a JPEG to PNG?</h4>
                  <p>
                    Technically JPEG is even compressible and goes to a smaller
                    file, but JPEGs are lossy with compression, and are not high
                    quality; therefore, this matters when you have a high
                    precision logo (or text); JPEGs have artifacts and shades so
                    the line you want sharp, might not be.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Can PNG handle complicated transparency?</h4>
                  <p>
                    Yes, PNG has alpha channels (256 levels of transparency) for
                    blended transparency and complicated effects.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Is PNG okay for photography?</h4>
                  <p>
                    It is an option, but PNG files are larger. PNG should only
                    be used when perfect quality is needed (this could be in the
                    case of edits need to be saved in a high-quality size, or if
                    an image needs to be archived).
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>What is the difference between PNG vs. PNG-24?</h4>
                  <p>
                    PNG-24 will support true color (16 million) and alpha
                    transparency vs. only 256.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Does PNG handle animation?</h4>
                  <p>
                    Standard PNG cannot, BUT APNG (animated PNG) can handle
                    animations and is of better quality than GIF.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Why does my PNG look different on Windows vs. MAC?</h4>
                  <p>
                    That is related to gamma correction on the two systems. PNG
                    embeds gamma data so that the colors will be maintained
                    across systems.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Editor Section */}
      {(image || convertedImage) && (
        <div className={styles.editorSection}>
          <div className={styles.mainContent}>
            {/* Editor Side */}
            <div className={styles.editorSide}>
              <div className={styles.sectionHeader}>
                <h3>Original Image</h3>
                <button onClick={resetAll} className={styles.changeImageButton}>
                  Change Image
                </button>
              </div>

              {image && (
                <>
                  <div className={styles.cropSection}>
                    <img
                      src={image.url}
                      alt="Original"
                      className={styles.cropImage}
                    />
                  </div>

                  <div className={styles.controls}>
                    <div className={styles.helpText}>
                      PNG format supports transparency and lossless compression
                    </div>

                    <div className={styles.buttonGroup}>
                      <button
                        onClick={convertToPNG}
                        className={styles.processButton}
                      >
                        Convert to PNG
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Preview Side */}
            {convertedImage && (
              <div className={styles.previewSide}>
                <div className={styles.previewSection}>
                  <h3>Converted PNG Image</h3>
                  <div className={styles.previewContent}>
                    <div className={styles.previewImageContainer}>
                      <img
                        src={convertedImage.url}
                        alt="Converted PNG"
                        className={styles.previewImage}
                      />
                    </div>

                    <div className={styles.imageInfo}>
                      <h4>File Information</h4>
                      <div className={styles.detailItem}>
                        <span>Format:</span>
                        <strong>PNG</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Compression:</span>
                        <strong>Lossless</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Dimensions:</span>
                        <strong>
                          {convertedImage.dimensions.width} ×{" "}
                          {convertedImage.dimensions.height}
                        </strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>File Size:</span>
                        <strong>{formatFileSize(convertedImage.size)}</strong>
                      </div>
                    </div>

                    <button
                      onClick={downloadImage}
                      className={styles.downloadButton}
                    >
                      Download PNG
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageToPNG;
